import api from './api';

export interface AuthConfig {
  type: 'api_key' | 'bearer' | 'basic' | 'oauth2' | 'custom';
  headerName?: string;
  headerValue?: string;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
  tokenUrl?: string;
  customHeaders?: Array<{
    name: string;
    value: string;
  }>;
}

export interface LegacyAuthConfig {
  headerName?: string;
  headerValue?: string;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
  tokenUrl?: string;
  customHeaders?: Array<{ name: string; value: string }>;
  authConfigs?: AuthConfig[];
  additionalHeaders?: Record<string, string>;
}

export interface EndpointParameter {
  name: string;
  type?: 'string' | 'number' | 'boolean';
  location?: 'query' | 'body' | 'header' | 'path';
  required: boolean;
  description?: string;
}

export interface APIProviderEndpoint {
  id?: string;
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  bodyType?: 'json' | 'x-www-form-urlencoded' | 'form-data' | 'none';
  description: string;
  parameters?: EndpointParameter[] | null;
}

export interface APIProvider {
  id?: string;
  name: string;
  description: string;
  baseUrl: string;
  documentation?: string;
  requiresAuth: boolean;
  authType?: 'none' | 'api_key' | 'bearer' | 'basic' | 'oauth2' | 'custom' | string;
  authConfig?: LegacyAuthConfig;
  authConfigs: AuthConfig[];
  rateLimit: number;
  timeout: number;
  isActive: boolean;
  lastTested?: string;
  testStatus?: 'success' | 'error' | 'pending';
  createdAt?: string;
  updatedAt?: string;
  endpoints: APIProviderEndpoint[];
  usage?: {
    total: number;
    today: number;
    thisMonth: number;
  };
}

export interface APIProviderStats {
  totalProviders: number;
  activeProviders: number;
  totalEndpoints: number;
  totalRequests: number;
}

export interface TestProviderResponse {
  success: boolean;
  message?: string;
  status?: number;
  responseTime?: number;
  data?: unknown;
}

export interface APIProviderLogItem {
  id: string;
  providerId: string;
  endpoint: string;
  method: string;
  url: string;
  status: number;
  duration: number;
  responseSize: number;
  success: boolean;
  error?: string;
  createdAt: string;
}

class APIProviderService {
  private getErrorMessage(error: unknown, fallback: string): string {
    if (error && typeof error === 'object' && 'response' in error) {
      const resp = (error as { response?: { data?: { message?: string } } }).response;
      if (resp?.data?.message) return resp.data.message;
    }
    if (error instanceof Error) return error.message;
    return fallback;
  }

  // Get all API providers
  async getAPIProviders(): Promise<APIProvider[]> {
    try {
      const response = await api.get<{ success: boolean; data: APIProvider[] }>('/api-providers');
      return response.data.data || [];
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to fetch API providers'));
    }
  }

  // Get single API provider
  async getAPIProvider(id: string): Promise<APIProvider> {
    try {
      const response = await api.get<{ success: boolean; data: APIProvider }>(`/api-providers/${id}`);
      return response.data.data;
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to fetch API provider'));
    }
  }

  // Create new API provider
  async createAPIProvider(provider: Omit<APIProvider, 'id'>): Promise<APIProvider> {
    try {
      const response = await api.post<{ success: boolean; data: APIProvider }>('/api-providers', provider);
      return response.data.data;
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to create API provider'));
    }
  }

  // Update API provider
  async updateAPIProvider(id: string, provider: Partial<APIProvider>): Promise<APIProvider> {
    try {
      const response = await api.put<{ success: boolean; data: APIProvider }>(`/api-providers/${id}`, provider);
      return response.data.data;
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to update API provider'));
    }
  }

  // Delete API provider
  async deleteAPIProvider(id: string): Promise<void> {
    try {
      await api.delete(`/api-providers/${id}`);
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to delete API provider'));
    }
  }

  // Toggle API provider status
  async toggleAPIProviderStatus(id: string, isActive: boolean): Promise<APIProvider> {
    try {
      const response = await api.patch<{ success: boolean; data: APIProvider }>(`/api-providers/${id}/status`, { isActive });
      return response.data.data;
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to update API provider status'));
    }
  }

  // Test API provider
  async testAPIProvider(id: string): Promise<TestProviderResponse> {
    try {
      const response = await api.post<TestProviderResponse>(`/api-providers/${id}/test`);
      return response.data;
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to test API provider'));
    }
  }

  // Get API provider stats
  async getAPIProviderStats(): Promise<APIProviderStats> {
    try {
      const response = await api.get<{ success: boolean; data: APIProviderStats }>('/api-providers/stats');
      return response.data.data;
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to fetch API provider stats'));
    }
  }

  // Get API provider logs
  async getAPIProviderLogs(id: string, filters?: Record<string, string | number>): Promise<APIProviderLogItem[]> {
    try {
      const response = await api.get<{ success: boolean; data: APIProviderLogItem[] }>(`/api-providers/${id}/logs`, { params: filters });
      return response.data.data || [];
    } catch (error: unknown) {
      throw new Error(this.getErrorMessage(error, 'Failed to fetch API provider logs'));
    }
  }
}

export const apiProviderService = new APIProviderService();